import { TierBadge } from "./StatusBadge";

// Colors mirror /app/backend/scoring.py::TIER_COLORS.
const TIER_STROKE = {
  Unrated: "#CBD5E1",
  "Below Threshold": "#DC2626",
  Bronze: "#B45309",
  Silver: "#64748B",
  Gold: "#D97706",
  Platinum: "#0E7490",
};

/**
 * ScoreGauge
 *  - score: numeric 0-100 or null when Unrated
 *  - tier / level: string (Unrated | Below Threshold | Bronze | Silver | Gold | Platinum)
 *  - state: "unrated" | "provisional" | "established" — provisional shows a dashed ring
 *  - size: pixel width of the SVG
 */
export default function ScoreGauge({ score = 0, tier, level, state = "established", size = 180, showBadge = true }) {
  const tierName = tier || level || "Unrated";
  const isUnrated = state === "unrated" || tierName === "Unrated" || score == null;
  const isProvisional = state === "provisional";

  const radius = (size - 16) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circ = 2 * Math.PI * radius;
  const numeric = Math.max(0, Math.min(100, Number(score) || 0));
  const offset = isUnrated ? circ : circ * (1 - numeric / 100);
  const color = TIER_STROKE[tierName] || TIER_STROKE.Unrated;

  return (
    <div className="flex flex-col items-center" data-testid="score-gauge">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={radius} className="gauge-track" strokeWidth="10" fill="none" />
        {!isUnrated && (
          <circle
            cx={cx}
            cy={cy}
            r={radius}
            stroke={color}
            strokeWidth="10"
            fill="none"
            strokeDasharray={isProvisional ? "6 6" : circ}
            strokeDashoffset={isProvisional ? 0 : offset}
            strokeLinecap="round"
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        )}
        {!isUnrated && !isProvisional && (
          <circle
            cx={cx}
            cy={cy}
            r={radius}
            stroke={color}
            strokeWidth="10"
            fill="none"
            strokeDasharray={circ}
            strokeDashoffset={offset}
            strokeLinecap="round"
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        )}
        <text
          x="50%"
          y="48%"
          textAnchor="middle"
          className="font-display"
          fill="#0F172A"
          style={{ fontSize: size >= 200 ? 44 : 36, fontWeight: 700 }}
        >
          {isUnrated ? "—" : Math.round(numeric)}
        </text>
        <text
          x="50%"
          y="62%"
          textAnchor="middle"
          fill="#64748B"
          style={{ fontSize: 11, letterSpacing: 1, textTransform: "uppercase" }}
        >
          {isUnrated ? "No rating yet" : "/ 100 score"}
        </text>
      </svg>
      {showBadge && (
        <div className="mt-2">
          <TierBadge tier={tierName} state={state} />
        </div>
      )}
    </div>
  );
}
